/**
 * Branded header that appears only on the printed sheet.
 *
 * Feedback (2026-09-29, Study details item 6) — the ask was a print option
 * rather than a download, "with the logo added". A plain window.print() gives
 * you the screen without any of that, so every printable view renders this at
 * the top of its print root: logo, what the sheet is, which study it belongs
 * to, and who printed it when.
 *
 * Invisible on screen (`.print-only` in globals.css). Keep the markup simple
 * and inline-styled where the value is dynamic — print stylesheets are the one
 * place where a missing utility class silently produces a blank sheet.
 */
export function PrintHeader({
  title,
  subtitle,
  printedBy,
}: {
  title: string;
  /** Usually the protocol number and study title. */
  subtitle?: string;
  /** Name or email of whoever hit Print. Omitted when unknown. */
  printedBy?: string;
}) {
  const printedAt = new Date().toLocaleString();
  return (
    <div className="print-only mb-4 border-b border-ink-primary/20 pb-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <img
            src="/DreamIT_logo.png"
            alt="DreamTrials"
            className="h-8 w-auto"
            // A broken image would otherwise print as a alt-text box in the
            // header of a regulated document.
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <span className="text-sm font-bold tracking-wide">DreamTrials</span>
        </div>
        <div className="text-right text-[10px] leading-4 text-ink-secondary">
          <div>Printed {printedAt}</div>
          {printedBy && <div>by {printedBy}</div>}
        </div>
      </div>
      <div className="mt-2">
        <div className="text-base font-semibold text-ink-primary">{title}</div>
        {subtitle && <div className="text-[11px] text-ink-secondary">{subtitle}</div>}
      </div>
    </div>
  );
}
